// src/components/Toolbar/FileActions/QuickExportButton.tsx
import React, { useState } from 'react';
import { Download, Check, AlertCircle } from 'lucide-react';
import { useExport } from '@features/export';
import { useModelStore } from '@stores/modelStore';
import { toast } from '@/stores/toastStore';

export const QuickExportButton: React.FC = () => {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const model = useModelStore((state) => ({
    version: state.version,
    title: state.title,
    description: state.description,
    colors: state.colors,
    icons: state.icons,
    items: state.items,
    views: state.views
  }));

  const { exportToPNG, isExporting } = useExport({
    onSuccess: () => {
      setStatus('success');
      toast.success('PNG exported successfully.');
      setTimeout(() => setStatus('idle'), 2000);
    },
    onError: (error) => {
      console.error('Quick export failed:', error);
      setStatus('error');
      toast.error(`Quick export failed: ${error.message}`);
      setTimeout(() => setStatus('idle'), 3000);
    }
  });

  const handleQuickExport = async () => {
    // Use the same canvas element selector as ExportDialog
    let canvasElement = document.querySelector(
      '[data-testid="isometric-canvas"]'
    ) as HTMLElement;

    if (!canvasElement) {
      canvasElement = document.querySelector(
        '.MuiBox-root[style*="position: relative"]'
      ) as HTMLElement;
    }

    if (!canvasElement) {
      toast.error('Canvas not found. Please ensure the diagram is loaded.');
      return;
    }

    // Export with default parameters: 2x quality, transparent background
    await exportToPNG(canvasElement, model, 2, false);
  };

  const getButtonContent = () => {
    if (isExporting) {
      return (
        <>
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          <span>Exporting...</span>
        </>
      );
    }

    if (status === 'success') {
      return (
        <>
          <Check className="h-4 w-4" />
          <span>Exported!</span>
        </>
      );
    }

    if (status === 'error') {
      return (
        <>
          <AlertCircle className="h-4 w-4" />
          <span>Failed</span>
        </>
      );
    }

    return (
      <>
        <Download className="h-4 w-4" />
        <span>Quick Export</span>
      </>
    );
  };

  const getButtonColor = () => {
    if (status === 'success') return 'bg-green-500 hover:bg-green-600';
    if (status === 'error') return 'bg-red-500 hover:bg-red-600';
    return 'bg-blue-500 hover:bg-blue-600';
  };

  // Check if canvas is empty
  const hasCanvasContent = model.items.length > 0;

  return (
    <button
      onClick={handleQuickExport}
      disabled={!hasCanvasContent || isExporting}
      data-quick-export
      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${getButtonColor()}`}
      title={!hasCanvasContent ? 'Cannot export empty canvas' : 'Quick export as PNG (2x quality, transparent background)'}
    >
      {getButtonContent()}
    </button>
  );
};
